'use client';

import { register } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useQuery } from '@tanstack/react-query';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import type { Control, FieldPath } from 'react-hook-form';
import type { InputProps } from '@eliadmin/core/src/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { fetchCompanyByICO } from '@/app/api/proform/registration-api';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { usePhoneInput } from 'react-international-phone';

const customerTypeValues = [
  {
    value: 'individual',
    label: 'text1113',
  },
  { value: 'company', label: 'text200' },
];

export const registerSchema = (VM: FormValidationMessages) =>
  z
    .object({
      name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      phone: z.string().min(5, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      dial: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      password: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),
      password_confirmation: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),
      country: z.object({
        id: z.string(),
      }),
      territory: z.object({
        id: z.string(),
      }),
      customerType: z.string().optional(),

      gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
      newsletter: z.boolean().optional(),
      ico: z.string().optional(),
      company: z.string().optional(),
      dic: z.string().optional(),
      ic_dph: z.string().optional(),

      newsletter_return_url: z.string(),
      tos: z.boolean().refine(val => val === true, { message: VM.TOS_VALIDATION_MESSAGE() }),
      'g-recaptcha-response': z.string().default(''),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

type RegisterFormProps = {
  initialSocialAuthProviders?: SocialAuthProviderItem[];
};

export function RegisterForm({ initialSocialAuthProviders }: RegisterFormProps) {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const countriesQuery = useQuery({
    queryKey: [CacheKey.COUNTRIES],
    queryFn: () =>
      fetchCountries({
        locale: 'cs',
        query: {
          results: 999,
        },
      }),
  });

  // const territoriesQuery = useQuery({
  //   queryKey: ['territories'],
  //   queryFn: () =>
  //     fetch('smako.cz/ZasShopServicePage.aspx/?/&command=GET_UZ_JEDNOTKY_FT&stat=CZ')
  //       .then((res: any) => res)
  //       .catch(e => console.log(e)),
  // });

  const form = useForm<z.infer<ReturnType<typeof registerSchema>>>({
    resolver: zodResolver(registerSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      dial: '',
      password: '',
      password_confirmation: '',
      gdpr: false,
      newsletter: false,
      tos: false,
      newsletter_return_url: new URL(
        ROUTES.NEWSLETTER_CONFIRM(),
        env.NEXT_PUBLIC_APP_URL,
      ).toString(),
      country: {
        id: '',
      },
      territory: {
        id: '',
      },
      customerType: 'individual',
      ico: '',
      company: '',
      dic: '',
      ic_dph: '',
    },
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const { toast } = useToast();
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    /** Check password validity */
    if (data.password !== data.password_confirmation) {
      form.setError('password_confirmation', { type: 'manual', message: t('text468') });
      form.setError('password', { type: 'manual', message: t('text468') });
      return;
    }

    if (data.phone.replace(data.dial, '').trim().startsWith('+')) {
      form.setError('phone', { type: 'manual', message: t('text1387') });
      return;
    }

    const transformedData = {
      email: data.email,
      password: data.password,
      password_confirmation: data.password_confirmation,
      name: data.name,
      surname: data.surname,
      company_buy: data.customerType === 'company',
      company: data.company,
      ico: data.ico,
      dic: data.dic,
      ic_dph: data.ic_dph,
      phone_preset: data.dial,
      phone: data.phone.replace(data.dial, '').trim(),
      country: data.country,

      gdpr: data.gdpr,
      newsletter: data.newsletter,
      newsletter_return_url: data.newsletter_return_url,
    };

    try {

      const resp = await register({
        ...transformedData,

        locale,
      } as any);
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      toast({
        title: t('text654'),
        variant: 'success',
      });
      router.replace(ROUTES.PROFILE());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text653'),
        description: (e as any).message,
        variant: 'destructive',
      });
    }
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  const handlePasswordMouseDown = () => {
    setShowPassword(true);
  };

  const handlePasswordMouseUp = () => {
    setShowPassword(false);
  };

  const handlePasswordConfirmationMouseDown = () => {
    setShowPasswordConfirmation(true);
  };

  const handlePasswordConfirmationMouseUp = () => {
    setShowPasswordConfirmation(false);
  };

  const countriesOptions = useMemo(
    () =>
      countriesQuery.data?.data.map(country => ({
        value: country.id,
        label: country.name,
      })) ?? [],
    [countriesQuery.data],
  );

  const [isLoadingCompanyInformation, setIsLoadingCompanyInformation] = useState(false);
  const [icoStatusMessage, setIcoStatusMessage] = useState('');

  const timerRef = useRef(null);

  useEffect(() => {
    form.setValue('company', '');
    form.setValue('dic', '');
    form.setValue('ic_dph', '');
  }, [form.watch('country.id')]);

  useEffect(() => {
    if (form.watch('country.id') !== 'CZ') return;
    // This function will be called every time `ico` changes
    const ico = form.watch('ico');

    if (!ico) return; // If there is no ico, we do nothing

    // Clear any existing timer when `ico` changes
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set up a new timer that delays the API call
    timerRef.current = setTimeout(() => {
      // Fetch data only if `ico` is not empty
      setIsLoadingCompanyInformation(true);
      fetchCompanyByICO(ico)
        .then(data => {
          if (data.obchodniJmeno) {
            setIcoStatusMessage('');
            setIsLoadingCompanyInformation(false);
            form.setValue('company', data.obchodniJmeno);
            form.setValue('dic', data.dic || '');
            return;
          }

          if (data.subKod) {
            if (data.subKod === 'VSTUP_NEVALIDNI_FORMAT_ICO') {
              setIcoStatusMessage('Neplatný formát IČO');
            }
            if (data.subKod === 'VYSTUP_SUBJEKT_NENALEZEN') {
              setIcoStatusMessage('Společnost nenalezena. Skontrolujte dle registru ARES.');
            }
          }
          setIsLoadingCompanyInformation(false);
          return;
        })
        .catch(e => {
          setIsLoadingCompanyInformation(false);
        });
    }, 500) as any; // Delay in milliseconds

    // Cleanup function to clear the timer if the component unmounts
    return () => {
      if (timerRef.current) {
        setIsLoadingCompanyInformation(false);
        clearTimeout(timerRef.current);
      }
    };
  }, [form.watch('ico'), form.watch('country.id')]);

  return (
    <Form {...form}>
      <form
        className={cn('flex w-full flex-col space-y-3', 'ea-register-form')}
        onSubmit={handleSubmit}
      >
        {/* header */}
        <div className={cn('mt-2 w-full max-w-[412px] px-2 py-2', 'ea-register-form__header')}>
          <h3
            className={cn('mb-4 text-center text-3xl font-bold', 'ea-register-form__header-title')}
          >
            {t('text261')}
          </h3>
          <p className={cn('mt-2 text-center', 'ea-register-form__link-wrapper')}>
            {t('text622')}{' '}
            <Link
              href={ROUTES.SIGN_IN()}
              className={cn('w-full text-center text-primary', 'ea-register-form__link')}
              prefetch={false}
            >
              {t('text623')}
            </Link>
          </p>
        </div>
        {/* content */}
        <SocialAuthButtons
          className="ea-register-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
          newsletterAccepted={form.watch('newsletter' as any)}
          newsletterReturnUrl={form.watch('newsletter_return_url' as any)}
        />

        <div className={cn('flex flex-col space-y-1', 'ea-register-form__content')}>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className={cn('', 'ea-register-form__email-field')}>
                <FormLabel>{t('text35')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('text35')}
                    className={{ wrapper: cn('rounded-lg'), main: 'rounded-lg' }}
                    type="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem required className={cn('', 'ea-register-form__password-field')}>
                <FormLabel>{t('text191')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      placeholder={t('text191')}
                      className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                      type={showPassword ? 'text' : 'password'}
                      {...field}
                    />
                    <button
                      type="button"
                      onMouseDown={handlePasswordMouseDown}
                      onMouseUp={handlePasswordMouseUp}
                      onMouseLeave={handlePasswordMouseUp} // In case the cursor leaves the button area while pressing
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5 focus:outline-none"
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </FormControl>
                <FormLabel
                  aria-required="false"
                  className="text-sm text-muted-foreground"
                  isRequired={false}
                >
                  {t('text630')}
                </FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password_confirmation"
            render={({ field }) => (
              <FormItem
                required
                className={cn('', 'ea-register-form__password-confirmation-field')}
              >
                <FormLabel>{t('text263')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      placeholder={t('text191')}
                      className={{ wrapper: cn('rounded-lg'), main: 'rounded-lg' }}
                      type={showPasswordConfirmation ? 'text' : 'password'}
                      {...field}
                    />
                    <button
                      type="button"
                      onMouseDown={handlePasswordConfirmationMouseDown}
                      onMouseUp={handlePasswordConfirmationMouseUp}
                      onMouseLeave={handlePasswordConfirmationMouseUp} // In case the cursor leaves the button area while pressing
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm leading-5 focus:outline-none"
                    >
                      {showPasswordConfirmation ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <RadioGroup
            className={cn('items-between flex w-full flex-wrap gap-6 pb-2 pt-4')}
            value={form.watch('customerType')}
            onValueChange={val => {
              form.setValue('customerType', val);
            }}
          >
            {customerTypeValues.map(customerType => (
              <div className="flex min-w-max flex-row items-center gap-2" key={customerType.value}>
                <RadioGroupItem value={customerType.value} id={`radio-${customerType.value}`} />
                <Label
                  htmlFor={`radio-${customerType.value}`}
                  className={cn(
                    'cursor-pointer text-muted-foreground',
                    form.watch('customerType') === customerType.value &&
                      'font-bold text-foreground',
                  )}
                >
                  {t(customerType.label)}
                </Label>
              </div>
            ))}
          </RadioGroup>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem required className={cn('', 'ea-register-form__name-field')}>
                <FormLabel>{t('text33')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('text33')}
                    className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="surname"
            render={({ field }) => (
              <FormItem required className={cn('', 'ea-register-form__surname-field')}>
                <FormLabel>{t('text34')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('text34')}
                    // rounded={AUTH_CONFIG.register.input.rounded}
                    className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Field
            control={form.control}
            name="country.id"
            label={t('text519')}
            placeholder={t('text904')}
            required
            type="select"
            options={countriesOptions}
            isLoading={countriesQuery.isLoading}
          />
        </div>

        <div
          className={cn(
            'hidden transition-all duration-300',
            form.watch('customerType') === 'company' && 'flex flex-col gap-2',
          )}
        >
          <FormField
            control={form.control}
            name="ico"
            render={({ field }) => (
              <FormItem required className={cn('', 'ea-register-form__name-field')}>
                <FormLabel>{t('text42')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('text42')}
                    className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                    {...field}
                    after={<>{isLoadingCompanyInformation && <Spinner />}</>}
                  />
                </FormControl>
                <FormMessage>
                  {form.watch('country.id') === 'CZ' && (
                    <div className="text-foreground">
                      Zkontroluje dle registru ARES a doplní dostupné informace Název, DIČ a adresu
                      společnosti.
                    </div>
                  )}
                  {icoStatusMessage && <div className="text-red-500">{icoStatusMessage}</div>}
                </FormMessage>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem required className={cn('', 'ea-register-form__name-field')}>
                <FormLabel>{t('text37')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('text37')}
                    readOnly={form.watch('country.id') === 'CZ'}
                    className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="dic"
            render={({ field }) => (
              <FormItem required className={cn('', 'ea-register-form__name-field')}>
                <FormLabel>{t('text43')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('text43')}
                    readOnly={form.watch('country.id') === 'CZ'}
                    className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.watch('country.id') === 'SK' && (
            <FormField
              control={form.control}
              name="ic_dph"
              render={({ field }) => (
                <FormItem required className={cn('', 'ea-register-form__name-field')}>
                  <FormLabel>{t('text44')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('text44')}
                      className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem required className={cn('', 'ea-register-form__phone-field')}>
              <FormLabel>{t('text36')}</FormLabel>
              <FormControl>
                <PhoneInputField
                  {...field}
                  onChange={(val, meta) => {
                    field.onChange(meta.inputValue);
                    form.setValue('dial', `+${meta.country.dialCode}`);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* FOOTER */}
        <div
          className={cn('flex flex-col items-start space-y-4 pb-12', 'ea-register-form__footer')}
        >
          <FormField
            control={form.control}
            name="tos"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-start space-x-3 space-y-0',
                  'ea-register-form__tos-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>
                  {t('text264')}{' '}
                  <Link href="/tos" className="underline" prefetch={false}>
                    {t('text265')}
                  </Link>
                </FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gdpr"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-start space-x-3 space-y-0',
                  'ea-register-form__gdpr-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>
                  <Link href="/gdpr" className="underline" prefetch={false}>
                    {t('text364')}
                  </Link>
                </FormLabel>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="newsletter"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-start space-x-3 space-y-0',
                  'ea-register-form__newsletter-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="border-none outline outline-1 outline-border"
                  />
                </FormControl>
                <FormLabel>{t('text629')}</FormLabel>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('w-full')}>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className={cn('h-12 w-full text-lg', 'ea-register-form__submit-button')}
            isLoading={form.formState.isSubmitting}
          >
            {t('text192')}
          </Button>
          <div>
            {/* TODO LINKS */}
            {t('text625')}{' '}
            <Link href={ROUTES.HOME()} className="text-primary" prefetch={false}>
              {t('text626')}
            </Link>{' '}
            {t('text627')}{' '}
            <Link href={ROUTES.HOME()} className="text-primary" prefetch={false}>
              {t('text628')}
            </Link>
          </div>
        </div>
      </form>
    </Form>
  );
}

export type RegisterFormValues = z.infer<ReturnType<typeof registerSchema>>;

type FieldProps = {
  control: Control<RegisterFormValues>;
  name: FieldPath<RegisterFormValues>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  type?: InputProps['type'] | 'multi-select' | 'phone' | 'checkbox';
  isFullWidth?: boolean;
  isDisabled?: boolean;
  after?: React.ReactNode;

  options?: { value: string | number; label: string }[];
  isLoading?: boolean;
};

function Field(props: FieldProps) {
  const { t } = useTranslations();

  const optionsMap = useMemo(() => {
    if (!props.options) {
      return {};
    }

    return props.options.reduce(
      (acc: any, option: any) => {
        acc[String(option.value)] = option;
        return acc;
      },
      {} as Record<string, { value: string | number; label: string }>,
    );
  }, [props.options]);

  const content = useMemo(() => {
    if (props.type === 'select') {
      if (!props.options) {
        throw new Error('Select type requires options prop');
      }

      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => {
            return (
              <FormItem required={props.required} className="p-[1px]">
                <FormLabel>{props.label}</FormLabel>
                <FormControl>
                  <Select
                    value={String(field.value)}
                    onValueChange={field.onChange}
                    disabled={props.isDisabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={props.placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {props.options?.map(o => (
                        <SelectItem key={o.value} value={String(o.value)}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      );
    }

    if (props.type === 'phone') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              {props.label && <FormLabel>{props.label}</FormLabel>}
              <FormControl>
                <div className="flex w-full flex-row items-center space-x-2">
                  <PhoneInputField
                    {...(field as any)}
                    placeholder={props.placeholder}
                    disabled={props.isDisabled || props.isLoading}
                    className={'w-full'}
                    required
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'checkbox') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required} className="flex w-full flex-col gap-2">
              <div className="gap-2s flex w-full flex-row items-center">
                <FormControl>
                  <Checkbox
                    checked={field.value as boolean}
                    onCheckedChange={checked => field.onChange(checked)}
                    disabled={props.isDisabled || props.isLoading}
                  />
                </FormControl>
                {props.label && <FormLabel>{props.label}</FormLabel>}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={props.control}
        name={props.name}
        render={({ field }) => (
          <FormItem required={props.required}>
            {props.label && <FormLabel>{props.label}</FormLabel>}
            <FormControl>
              <div className="flex w-full flex-row items-center space-x-2">
                <Input
                  placeholder={props.placeholder}
                  type={props.type}
                  {...(field as any)}
                  disabled={props.isDisabled || props.isLoading}
                  aria-disabled={props.isDisabled || props.isLoading}
                  afterOuter={props.after}
                  className={{ wrapper: 'w-auto flex-1' }}
                />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }, [
    optionsMap,
    props.after,
    props.control,
    props.isDisabled,
    props.isLoading,
    props.label,
    props.name,
    props.options,
    props.placeholder,
    props.required,
    props.type,
    t,
  ]);

  return (
    <div
      className={cn('col-span-12 md:col-span-6', {
        ['md:col-span-12']: props.isFullWidth,
      })}
    >
      {content}
    </div>
  );
}
